import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { AdminOverviewDashboard } from '../../../components/admin/AdminOverviewFidelity';
import { AdminRoleDashboard } from '../../../components/admin/AdminRoleDashboards';
import { Alert } from '../../../components/Alert';
import { DashboardVariantTabs } from '../../../components/DashboardVariantTabs';
import { PageSpinner } from '../../../components/PageSpinner';
import { useAuth } from '../../../features/auth/auth-context';
import {
  STAFF_DASHBOARD_VARIANT_LABELS,
  canAccessDashboardVariant,
  getAccessibleDashboardVariants,
  resolveDefaultStaffDashboardVariant,
  type StaffDashboardVariant,
} from '../../../features/auth/portal-access';
import { reportingApi } from '../../../features/reporting/reporting.api';
import { toDisplayMessage } from '../../../lib/error-message';

import styles from './AdminPortal.module.css';

export function AdminOperationalDashboardPage(): React.JSX.Element {
  const { can, user } = useAuth();
  const accessibleVariants = useMemo(() => getAccessibleDashboardVariants(can), [can]);
  const defaultVariant = useMemo(() => resolveDefaultStaffDashboardVariant(can), [can]);
  const [selectedVariant, setSelectedVariant] = useState<StaffDashboardVariant>(
    defaultVariant ?? 'overview',
  );

  const activeVariant =
    accessibleVariants.includes(selectedVariant) && canAccessDashboardVariant(selectedVariant, can)
      ? selectedVariant
      : (defaultVariant ?? accessibleVariants[0] ?? 'overview');

  const dashboard = useQuery({
    queryKey: ['admin', 'dashboard', activeVariant],
    queryFn: () => reportingApi.getDashboardByVariant(activeVariant),
    enabled: canAccessDashboardVariant(activeVariant, can),
  });

  const isOverview = activeVariant === 'overview';
  const roleLabel = user?.roles[0]?.name ?? 'Operations team';
  const navigation = (
    <DashboardVariantTabs
      variants={accessibleVariants}
      active={activeVariant}
      onChange={setSelectedVariant}
    />
  );

  return (
    <div className={styles.page}>
      {!canAccessDashboardVariant(activeVariant, can) && (
        <Alert tone="info">Your account cannot load this dashboard view.</Alert>
      )}

      {canAccessDashboardVariant(activeVariant, can) && dashboard.isPending && (
        <>
          {navigation}
          <PageSpinner
            label={`Loading ${STAFF_DASHBOARD_VARIANT_LABELS[activeVariant].toLowerCase()} dashboard`}
          />
        </>
      )}
      {dashboard.isError && <Alert tone="error">{toDisplayMessage(dashboard.error)}</Alert>}

      {dashboard.isSuccess && isOverview && (
        <AdminOverviewDashboard
          generatedAt={dashboard.data.generatedAt}
          cards={dashboard.data.cards}
          summary={dashboard.data.summary}
          pipeline={dashboard.data.pipeline}
          financial={dashboard.data.financial}
          attention={dashboard.data.attention}
          recent={dashboard.data.recent}
          userEmail={user?.email}
          roleLabel={roleLabel}
          can={can}
          navigation={navigation}
        />
      )}

      {dashboard.isSuccess && !isOverview && (
        <AdminRoleDashboard
          variant={activeVariant}
          dashboard={dashboard.data}
          userEmail={user?.email}
          roleLabel={roleLabel}
          can={can}
          navigation={navigation}
        />
      )}
    </div>
  );
}
